import { cssThemedValue } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import React, { useMemo } from 'react';
import { FieldInputProps, FieldMetaState, FieldRenderProps } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

type Props<FieldValueT> = {
  children: (input: FieldInputProps<FieldValueT>) => React.ReactNode;
  className?: string;
  field: FieldRenderProps<FieldValueT>;
  /** Set as true to show errors immediately, for edit forms for example, default: false */
  showErrorImmediately?: boolean;
  /** Optional: custom method to show error based on the form meta data */
  showError?: (meta: FieldMetaState<FieldValueT>) => boolean;
  style?: React.CSSProperties;
};

const defaultShowError = <FieldValueT,>(meta: FieldMetaState<FieldValueT>): boolean => {
  if (meta.submitError) {
    return !meta.dirtySinceLastSubmit;
  }

  return !!meta.touched;
};

const formatError = (error: any) => {
  if (typeof error === 'string') {
    return error;
  }
  if (error?.id) {
    return <FormattedMessage {...error} />;
  }
  return JSON.stringify(error);
};

const Root = styled.div`
  color: ${cssThemedValue('textPrimary')};
`;

const InputContainer = styled.div``;

const StyledInputAlert = styled(InputAlert)`
  margin-block-start: 8px;
`;

/** Error wrapper to be used along with React Final Form Field */
const RFFFieldErrorWrapper = <FieldValueT,>({
  children,
  className,
  field,
  showErrorImmediately = false,
  showError = defaultShowError,
  style,
}: Props<FieldValueT>) => {
  const error = field.meta.error || field.meta.submitError;
  const invalid = useMemo(
    () => !!error && (showErrorImmediately || showError(field.meta)),
    [error, field.meta, showError, showErrorImmediately],
  );
  const renderProps = useMemo(() => ({ ...field.input, invalid }), [field.input, invalid]);
  return (
    <Root className={className} style={style}>
      <InputContainer>{children(renderProps)}</InputContainer>
      {invalid && <StyledInputAlert variant="error">{formatError(error)}</StyledInputAlert>}
    </Root>
  );
};

export default RFFFieldErrorWrapper;
